import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { TIMEOUT_EXTRACAO_IA } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Save, Package, Check, DollarSign, TrendingUp, Weight, Plus, Trash2, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { validarQuantidadePositiva } from '../lib/validators';

const STATUS_OPTIONS = ['PENDENTE', 'IMPLANTADO', 'NF_EMITIDA', 'CANCELADO'];
const STATUS_CONFIG = {
  PENDENTE:   { label: 'Pendente',   cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  IMPLANTADO: { label: 'Implantado', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  NF_EMITIDA: { label: 'NF Emitida', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  CANCELADO:  { label: 'Cancelado',  cls: 'bg-slate-100 text-slate-500 border-slate-300' },
};

const FORM_INITIAL = {
  numero_pedido_fabrica: '',
  cliente_nome: '',
  cliente_id: '',
  numero_oc: '',
  data_entrega: '',
  status: 'PENDENTE',
  condicao_pagamento: '',
  representada_id: '',
  representada_nome: '',
};

const ITEM_INITIAL = {
  numero_fe: '',
  item_nome: '',
  quantidade: '',
  peso_unit: '',
  preco_milheiro: '',
  peso_total: '',
  fator: '',
  valor_total: '',
  comissao_percent: '',
  comissao_valor: '',
};

const inp = "w-full border border-slate-200 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-[#0A3D73] focus:ring-2 focus:ring-[#0A3D73]/10 transition-all";
const lbl = "text-xs font-bold uppercase tracking-wide text-slate-400 mb-1 block";

const fmt = (v, c = 2) => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c }).format(v || 0);
const toNum = (v) => { if (!v && v !== 0) return 0; if (typeof v === 'number') return v; return parseFloat(String(v).replace(/\./g, '').replace(',', '.')) || 0; };
const brl = (v) => toNum(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const calcFromMat = (mat, qtdeStr) => {
  const pesoUnit = toNum(mat.peso_unit);
  const precoMilheiro = toNum(mat.preco_unit);
  const comissaoPercent = toNum(mat.comissao);
  const qtde = toNum(qtdeStr);
  const pesoTotal = qtde * pesoUnit;
  const valorTotal = (qtde / 1000) * precoMilheiro;
  const fator = pesoUnit > 0 ? precoMilheiro / (pesoUnit * 1000) : 0;
  const comissaoValor = valorTotal * comissaoPercent / 100;
  return {
    pesoUnit,
    precoMilheiro,
    comissaoPercent,
    pesoTotal: qtde > 0 ? fmt(pesoTotal) : '',
    valorTotal: qtde > 0 ? fmt(valorTotal) : '',
    fator: fmt(fator),
    comissaoValor: qtde > 0 ? fmt(comissaoValor) : '',
  };
};

const PedidoForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState(FORM_INITIAL);
  const [initialData, setInitialData] = useState(FORM_INITIAL);
  const [itens, setItens] = useState([]);
  const [initialItens, setInitialItens] = useState([]);
  const [itemEditando, setItemEditando] = useState(ITEM_INITIAL);
  const [adicionandoItem, setAdicionandoItem] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [materiais, setMateriais] = useState([]);
  const [condicoes, setCondicoes] = useState([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [clienteInput, setClienteInput] = useState('');
  const [showClientesDrop, setShowClientesDrop] = useState(false);
  const dropRef = useRef(null);

  const [sacDialog, setSacDialog] = useState(false);
  const [sacStep, setSacStep] = useState('upload');
  const [sacLoading, setSacLoading] = useState(false);
  const [sacExtract, setSacExtract] = useState([]);
  const [sacPreenchido, setSacPreenchido] = useState({});

  const inpCls = (field) => sacPreenchido[field]
    ? 'w-full border-2 border-emerald-400 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-400/10 transition-all'
    : inp;

  useEffect(() => {
    const init = async () => {
      try {
        const [cRes, mRes, cpRes] = await Promise.all([
          api.get('/clientes').catch(() => ({ data: [] })),
          api.get('/materiais').catch(() => ({ data: [] })),
          api.get('/condicoes-pagamento').catch(() => ({ data: [] })),
        ]);
        const materiasList = Array.isArray(mRes.data) ? mRes.data : [];
        const clientesList = Array.isArray(cRes.data) ? cRes.data : [];
        setClientes(clientesList);
        setMateriais(materiasList);
        setCondicoes(Array.isArray(cpRes.data) ? cpRes.data : []);

        if (isEditing) {
          const pRes = await api.get(`/pedidos/${id}`);
          const p = pRes.data;
          const data = {
            numero_pedido_fabrica: p.numero_fabrica || '',
            cliente_nome: p.cliente_nome || '',
            cliente_id: p.cliente_id || '',
            numero_oc: p.numero_oc || '',
            data_entrega: p.data_entrega || '',
            status: p.status || 'PENDENTE',
            condicao_pagamento: p.condicao_pagamento || '',
            representada_id: p.representada_id || '',
            representada_nome: p.representada_nome || '',
          };
          setFormData(data);
          setInitialData(data);
          setClienteInput(p.cliente_nome || '');

          let itensUI = [];
          if (p.itens && p.itens.length > 0) {
            itensUI = p.itens.map((it, i) => {
              const qtde = parseInt(it.quantidade) || 0;
              const pesoTotal = toNum(it.peso_calculado);
              const pesoUnit = qtde > 0 ? pesoTotal / qtde : 0;
              const precoMilheiro = toNum(it.valor_unitario);
              const fator = pesoUnit > 0 ? precoMilheiro / (pesoUnit * 1000) : 0;
              const feCode = it.numero_fe || (i === 0 ? p.numero_fe : '') || '';
              const mat = materiasList.find(m => m.numero_fe?.toUpperCase() === feCode?.toUpperCase());
              return {
                numero_fe: feCode,
                item_nome: it.item_nome || (i === 0 ? p.item_nome : '') || '',
                quantidade: String(qtde),
                peso_unit: mat ? String(mat.peso_unit || '') : (pesoUnit > 0 ? String(pesoUnit) : ''),
                preco_milheiro: precoMilheiro > 0 ? fmt(precoMilheiro) : '',
                peso_total: pesoTotal > 0 ? fmt(pesoTotal) : '',
                fator: fmt(fator),
                valor_total: toNum(it.subtotal) > 0 ? fmt(toNum(it.subtotal)) : '',
                comissao_percent: it.comissao_percent > 0 ? fmt(it.comissao_percent) : '',
                comissao_valor: it.comissao_valor > 0 ? fmt(it.comissao_valor) : '',
              };
            });
          } else {
            // backward compat: pedidos antigos sem array itens
            const mat = materiasList.find(m =>
              m.numero_fe?.toUpperCase() === p.numero_fe?.toUpperCase() ||
              m.codigo?.toUpperCase() === p.numero_fe?.toUpperCase()
            );
            const qtde = parseInt(p.quantidade) || 0;
            const pesoTotal = toNum(p.peso_total);
            const pesoUnit = mat ? toNum(mat.peso_unit) : (qtde > 0 ? pesoTotal / qtde : 0);
            const precoMilheiro = mat ? toNum(mat.preco_unit) : 0;
            const fator = pesoUnit > 0 ? precoMilheiro / (pesoUnit * 1000) : 0;
            const comissaoPercent = mat ? toNum(mat.comissao) : 0;
            itensUI = [{
              numero_fe: p.numero_fe || '',
              item_nome: p.item_nome || '',
              quantidade: String(qtde),
              peso_unit: pesoUnit > 0 ? String(pesoUnit) : '',
              preco_milheiro: precoMilheiro > 0 ? fmt(precoMilheiro) : '',
              peso_total: pesoTotal > 0 ? fmt(pesoTotal) : '',
              fator: fmt(fator),
              valor_total: toNum(p.valor_total) > 0 ? fmt(toNum(p.valor_total)) : '',
              comissao_percent: comissaoPercent > 0 ? fmt(comissaoPercent) : '',
              comissao_valor: toNum(p.comissao_valor) > 0 ? fmt(toNum(p.comissao_valor)) : '',
            }];
          }
          setItens(itensUI);
          setInitialItens(itensUI);
        }
      } catch {
        toast.error('Pedido não encontrado');
        navigate('/pedidos');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setShowClientesDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const listaClientes = useMemo(() => {
    const names = clientes.map(c => c.nome?.toUpperCase()).filter(Boolean);
    return [...new Set(names)].sort();
  }, [clientes]);

  const totaisItens = useMemo(() => ({
    quantidade: itens.reduce((a, it) => a + (parseInt(it.quantidade) || 0), 0),
    pesoTotal: itens.reduce((a, it) => a + toNum(it.peso_total), 0),
    valorTotal: itens.reduce((a, it) => a + toNum(it.valor_total), 0),
    comissaoValor: itens.reduce((a, it) => a + toNum(it.comissao_valor), 0),
  }), [itens]);

  const handleItemFEChange = (fe) => {
    const valor = fe.toUpperCase();
    const mat = materiais.find(m => m.numero_fe?.toUpperCase() === valor || m.codigo?.toUpperCase() === valor);
    if (mat) {
      const c = calcFromMat(mat, itemEditando.quantidade);
      setItemEditando(prev => ({
        ...prev,
        numero_fe: valor,
        item_nome: mat.nome || mat.descricao || '',
        peso_unit: String(mat.peso_unit || ''),
        preco_milheiro: String(mat.preco_unit || '').replace('.', ','),
        comissao_percent: String(mat.comissao || '').replace('.', ','),
        peso_total: c.pesoTotal,
        valor_total: c.valorTotal,
        fator: c.fator,
        comissao_valor: c.comissaoValor,
      }));
      setFormData(prev => ({
        ...prev,
        representada_id: mat.representada_id || prev.representada_id,
        representada_nome: mat.representada_nome || prev.representada_nome,
        ...(!prev.cliente_id && mat.cliente_id ? {
          cliente_nome: mat.cliente_nome || '',
          cliente_id: mat.cliente_id || '',
        } : {}),
      }));
      if (!formData.cliente_id && mat.cliente_id) {
        setClienteInput(mat.cliente_nome || '');
      }
    } else {
      setItemEditando(prev => ({ ...prev, numero_fe: valor }));
    }
  };

  const handleItemQtdeChange = (qtdeStr) => {
    const fe = itemEditando.numero_fe;
    const mat = materiais.find(m => m.numero_fe?.toUpperCase() === fe || m.codigo?.toUpperCase() === fe);
    if (mat) {
      const c = calcFromMat(mat, qtdeStr);
      setItemEditando(prev => ({
        ...prev,
        quantidade: qtdeStr,
        peso_total: c.pesoTotal,
        valor_total: c.valorTotal,
        fator: c.fator,
        comissao_valor: c.comissaoValor,
      }));
    } else {
      const pesoUnit = toNum(itemEditando.peso_unit);
      const precoMilheiro = toNum(itemEditando.preco_milheiro);
      const comissaoPercent = toNum(itemEditando.comissao_percent);
      const qtde = toNum(qtdeStr);
      const pesoTotal = qtde * pesoUnit;
      const valorTotal = (qtde / 1000) * precoMilheiro;
      const fator = pesoUnit > 0 ? precoMilheiro / (pesoUnit * 1000) : 0;
      setItemEditando(prev => ({
        ...prev,
        quantidade: qtdeStr,
        peso_total: qtde > 0 ? fmt(pesoTotal) : '',
        valor_total: qtde > 0 ? fmt(valorTotal) : '',
        fator: fmt(fator),
        comissao_valor: qtde > 0 ? fmt(valorTotal * comissaoPercent / 100) : '',
      }));
    }
  };

  const converterDataSac = (dataStr) => {
    const m = String(dataStr || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
  };

  const aplicarConfirmacaoSac = (c) => {
    const dataConvertida = converterDataSac(c.data_entrega);
    const preenchidos = {};

    if (!formData.numero_pedido_fabrica && c.numero_pedido) preenchidos.numero_pedido_fabrica = true;
    if (!formData.numero_oc && c.oc) preenchidos.numero_oc = true;
    if (!formData.data_entrega && dataConvertida) preenchidos.data_entrega = true;

    setFormData(prev => ({
      ...prev,
      ...(preenchidos.numero_pedido_fabrica ? { numero_pedido_fabrica: String(c.numero_pedido) } : {}),
      ...(preenchidos.numero_oc ? { numero_oc: String(c.oc) } : {}),
      ...(preenchidos.data_entrega ? { data_entrega: dataConvertida } : {}),
    }));

    if (!adicionandoItem) setAdicionandoItem(true);

    const feVazio = !itemEditando.numero_fe;
    const qtdeVazia = !itemEditando.quantidade;

    if (feVazio && c.numero_fe) {
      // Mesma lógica de handleItemFEChange, combinada com a quantidade num único
      // update para evitar leitura de itemEditando desatualizado (state assíncrono).
      const valor = c.numero_fe.toUpperCase();
      const mat = materiais.find(m => m.numero_fe?.toUpperCase() === valor || m.codigo?.toUpperCase() === valor);
      const qtdeAplicada = (qtdeVazia && c.qtde) ? String(c.qtde) : itemEditando.quantidade;
      if (mat) {
        const calc = calcFromMat(mat, qtdeAplicada);
        setItemEditando(prev => ({
          ...prev,
          numero_fe: valor,
          item_nome: mat.nome || mat.descricao || '',
          peso_unit: String(mat.peso_unit || ''),
          preco_milheiro: String(mat.preco_unit || '').replace('.', ','),
          comissao_percent: String(mat.comissao || '').replace('.', ','),
          quantidade: qtdeAplicada,
          peso_total: calc.pesoTotal,
          valor_total: calc.valorTotal,
          fator: calc.fator,
          comissao_valor: calc.comissaoValor,
        }));
        setFormData(prev => ({
          ...prev,
          representada_id: mat.representada_id || prev.representada_id,
          representada_nome: mat.representada_nome || prev.representada_nome,
          ...(!prev.cliente_id && mat.cliente_id ? { cliente_nome: mat.cliente_nome || '', cliente_id: mat.cliente_id || '' } : {}),
        }));
        if (!formData.cliente_id && mat.cliente_id) setClienteInput(mat.cliente_nome || '');
      } else {
        setItemEditando(prev => ({ ...prev, numero_fe: valor, quantidade: qtdeAplicada }));
      }
      preenchidos.numero_fe = true;
      if (qtdeVazia && c.qtde) preenchidos.quantidade = true;
    } else if (qtdeVazia && c.qtde) {
      handleItemQtdeChange(String(c.qtde));
      preenchidos.quantidade = true;
    }

    setSacPreenchido(prev => ({ ...prev, ...preenchidos }));
    const count = Object.keys(preenchidos).length;
    if (count > 0) toast.success(`✓ ${count} campo(s) preenchido(s) via SAC`);
    else toast.error('Todos os campos já estavam preenchidos manualmente — nada foi alterado');
  };

  const handleImagemSelecionadaSac = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataUrl = e.target.result;
      const [header, b64] = dataUrl.split(',');
      const mediaType = header.match(/:(.*?);/)?.[1] || 'image/jpeg';
      setSacLoading(true);
      try {
        const resp = await api.post('/pedidos/extrair-confirmacoes', { imagem_b64: b64, media_type: mediaType }, { timeout: TIMEOUT_EXTRACAO_IA });
        const lista = resp.data?.confirmacoes;
        if (!Array.isArray(lista) || lista.length === 0) {
          toast.error('Nenhum dado encontrado na imagem. Tente com outra imagem.');
          return;
        }
        if (lista.length === 1) {
          aplicarConfirmacaoSac(lista[0]);
          setSacDialog(false);
        } else {
          setSacExtract(lista);
          setSacStep('select');
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erro ao extrair dados da imagem');
      } finally {
        setSacLoading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFecharSacDialog = () => {
    setSacDialog(false);
    setSacStep('upload');
    setSacExtract([]);
  };

  const handleConfirmarItem = () => {
    const fe = String(itemEditando.numero_fe ?? '').trim();
    if (!fe || fe === '0') {
      toast.error('Informe um FE válido');
      return;
    }
    const valida = validarQuantidadePositiva(itemEditando.quantidade);
    if (!valida.ok) {
      toast.error(valida.erro);
      return;
    }
    if (!(toNum(itemEditando.preco_milheiro) > 0)) {
      toast.error('Informe um preço por milheiro maior que zero');
      return;
    }
    setItens(prev => [...prev, { ...itemEditando }]);
    setItemEditando(ITEM_INITIAL);
    setAdicionandoItem(false);
  };

  const handleRemoverItem = (idx) => {
    setItens(prev => prev.filter((_, i) => i !== idx));
  };

  const isDirty = JSON.stringify(formData) !== JSON.stringify(initialData) || JSON.stringify(itens) !== JSON.stringify(initialItens);

  const handleBack = () => {
    if (isDirty && !window.confirm('Há alterações não salvas. Deseja sair mesmo assim?')) return;
    navigate('/pedidos');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.cliente_id) {
      toast.error("Selecione o cliente do cadastro");
      return;
    }
    if (itens.length === 0) {
      toast.error("Adicione pelo menos um item ao pedido");
      return;
    }
    for (const it of itens) {
      const fe = String(it.numero_fe ?? '').trim();
      if (!fe || fe === '0') {
        toast.error('Há item sem FE válido. Corrija antes de salvar.');
        return;
      }
      const valida = validarQuantidadePositiva(it.quantidade);
      if (!valida.ok) {
        toast.error(`Item "${it.item_nome || 'sem nome'}": ${valida.erro}`);
        return;
      }
      if (!(toNum(it.preco_milheiro) > 0)) {
        toast.error(`Item "${it.item_nome || 'sem nome'}": preço por milheiro deve ser maior que zero`);
        return;
      }
    }
    setSaving(true);
    try {
      const primeiroItem = itens[0];
      const payload = {
        cliente_nome: formData.cliente_nome.toUpperCase(),
        item_nome: primeiroItem.item_nome?.toUpperCase() || '',
        numero_fabrica: formData.numero_pedido_fabrica,
        numero_fe: primeiroItem.numero_fe,
        numero_oc: formData.numero_oc,
        data_entrega: formData.data_entrega,
        quantidade: totaisItens.quantidade,
        peso_total: totaisItens.pesoTotal,
        valor_total: totaisItens.valorTotal,
        comissao_valor: totaisItens.comissaoValor,
        status: formData.status,
        condicao_pagamento: formData.condicao_pagamento,
        cliente_id: formData.cliente_id,
        representada_id: formData.representada_id,
        representada_nome: formData.representada_nome,
        itens: itens.map(it => ({
          material_id: '',
          quantidade: parseInt(it.quantidade) || 0,
          peso_calculado: toNum(it.peso_total),
          valor_unitario: toNum(it.preco_milheiro),
          subtotal: toNum(it.valor_total),
          comissao_percent: toNum(it.comissao_percent),
          comissao_valor: toNum(it.comissao_valor),
          ipi: 0,
          numero_fe: it.numero_fe,
          item_nome: it.item_nome,
          fator: toNum(it.fator),
        })),
      };
      if (isEditing) {
        await api.put(`/pedidos/${id}`, payload);
        toast.success('Pedido atualizado!');
      } else {
        await api.post('/pedidos', payload);
        toast.success('Pedido registrado!');
      }
      navigate('/pedidos');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar pedido');
    } finally {
      setSaving(false);
    }
  };

  const statusCfg = STATUS_CONFIG[formData.status] || STATUS_CONFIG.PENDENTE;

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin" />
      <p className="text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">Carregando Pedido...</p>
    </div>
  );

  return (
    <form id="pedido-form" onSubmit={handleSubmit} className="min-h-screen bg-[#E9EEF2]">

      {/* MODAL — Importar do SAC */}
      {sacDialog && (
        <Dialog open={sacDialog} onOpenChange={(o) => { if (!o) handleFecharSacDialog(); }}>
          <DialogContent className="max-w-2xl bg-white rounded-2xl p-0 overflow-hidden border-none shadow-2xl">
            <DialogHeader className="p-5 bg-[#0A3D73] text-white">
              <DialogTitle className="text-xs uppercase tracking-widest flex items-center gap-2 font-black">
                <Upload size={15} />
                {sacStep === 'upload' && 'Importar do SAC'}
                {sacStep === 'select' && `Selecione a linha correspondente — ${sacExtract.length} encontrada(s)`}
              </DialogTitle>
            </DialogHeader>

            {sacStep === 'upload' && (
              <div className="p-6 space-y-5">
                <p className="text-xs font-semibold text-slate-500">
                  Faça upload do print do SAC da fábrica. A IA vai extrair os dados da tabela e preencher os campos vazios deste pedido.
                </p>
                {sacLoading ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-4">
                    <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin" />
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Analisando imagem com IA...</p>
                  </div>
                ) : (
                  <label className="block cursor-pointer">
                    <div className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center hover:border-[#0A3D73] hover:bg-blue-50 transition-all">
                      <Upload size={32} className="mx-auto text-slate-300 mb-3" />
                      <p className="text-sm font-black text-slate-600">Clique para selecionar ou arraste a imagem</p>
                      <p className="text-xs text-slate-400 font-semibold mt-1">PNG, JPG, JPEG — máx. 5 MB</p>
                    </div>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      className="hidden"
                      onChange={(e) => { handleImagemSelecionadaSac(e.target.files?.[0]); e.target.value = ''; }}
                    />
                  </label>
                )}
                <div className="flex justify-end pt-2 border-t border-slate-100">
                  <button type="button" onClick={handleFecharSacDialog}
                    className="rounded-xl text-xs font-black uppercase h-9 px-5 border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors">
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {sacStep === 'select' && (
              <div className="p-6 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700 font-semibold">
                  A imagem tem mais de uma linha. Clique na linha que corresponde a este pedido.
                </div>
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-3 py-2 text-left font-black text-slate-500 uppercase tracking-wide">Nº Pedido</th>
                        <th className="px-3 py-2 text-left font-black text-slate-500 uppercase tracking-wide">FE</th>
                        <th className="px-3 py-2 text-left font-black text-slate-500 uppercase tracking-wide">OC</th>
                        <th className="px-3 py-2 text-left font-black text-slate-500 uppercase tracking-wide">Dt. Entrega</th>
                        <th className="px-3 py-2 text-right font-black text-slate-500 uppercase tracking-wide">Qtde</th>
                        <th className="px-3 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {sacExtract.map((c, i) => (
                        <tr key={i}
                          onClick={() => { aplicarConfirmacaoSac(c); handleFecharSacDialog(); }}
                          className={`${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-blue-50 cursor-pointer transition-colors`}>
                          <td className="px-3 py-2 font-mono font-bold text-slate-800">{c.numero_pedido}</td>
                          <td className="px-3 py-2 font-mono text-slate-600">{c.numero_fe || '—'}</td>
                          <td className="px-3 py-2 text-slate-600">{c.oc || '—'}</td>
                          <td className="px-3 py-2 text-slate-600">{c.data_entrega || '—'}</td>
                          <td className="px-3 py-2 text-right font-bold text-slate-800">
                            {c.qtde ? Number(c.qtde).toLocaleString('pt-BR') : '—'}
                          </td>
                          <td className="px-3 py-2 text-right"><Check size={14} className="text-[#0A3D73]" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
                  <button type="button" onClick={() => { setSacStep('upload'); setSacExtract([]); }}
                    className="rounded-xl text-xs font-black uppercase h-9 px-5 border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors">
                    Voltar
                  </button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* HEADER */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <button type="button" onClick={handleBack}
              className="flex items-center gap-1.5 text-xs font-black uppercase text-slate-500 hover:text-[#0A3D73] transition-colors">
              <ArrowLeft size={14} /> Voltar
            </button>
            <div className="w-px h-5 bg-slate-200" />
            <div>
              <h1 className="text-base font-black text-slate-900">
                {isEditing ? `Editar Pedido — ${formData.numero_pedido_fabrica || id}` : 'Novo Pedido'}
              </h1>
              <p className="text-[11px] text-slate-400 font-semibold">Carteira de Pedidos</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setSacDialog(true)}
              className="flex items-center gap-2 bg-white border border-[#0A3D73] text-[#0A3D73] hover:bg-blue-50 text-xs font-black uppercase px-4 py-2.5 rounded-xl transition-colors">
              <Upload size={14} /> Importar do SAC
            </button>
            <button
              type="submit"
              form="pedido-form"
              disabled={saving}
              className="flex items-center gap-2 bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase px-5 py-2.5 rounded-xl shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <Save size={14} /> {saving ? 'Salvando...' : 'Gravar Pedido'}
            </button>
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-6 max-w-6xl mx-auto">

          {/* ── COL ESQUERDA ── */}
          <div className="space-y-6">

            {/* Card 01 · Identificação */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">01 · Identificação</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>Nº Pedido Fábrica {sacPreenchido.numero_pedido_fabrica && <span className="text-emerald-500 normal-case font-black">✓ SAC</span>}</label>
                  <input
                    value={formData.numero_pedido_fabrica}
                    onChange={e => setFormData(prev => ({ ...prev, numero_pedido_fabrica: e.target.value }))}
                    className={inpCls('numero_pedido_fabrica')}
                    placeholder="Após implantação" />
                </div>
                <div>
                  <label className={lbl}>OC Cliente {sacPreenchido.numero_oc && <span className="text-emerald-500 normal-case font-black">✓ SAC</span>}</label>
                  <input
                    value={formData.numero_oc}
                    onChange={e => setFormData(prev => ({ ...prev, numero_oc: e.target.value }))}
                    className={inpCls('numero_oc')} />
                </div>
              </div>
            </div>

            {/* Card 02 · Cliente & Entrega */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">02 · Cliente & Entrega</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative md:col-span-2" ref={dropRef}>
                  <label className={lbl}>Cliente *</label>
                  <input
                    value={clienteInput}
                    onChange={e => {
                      setClienteInput(e.target.value);
                      setFormData(prev => ({ ...prev, cliente_nome: e.target.value.toUpperCase(), cliente_id: '' }));
                      setShowClientesDrop(true);
                    }}
                    onFocus={() => setShowClientesDrop(true)}
                    className={inp}
                    required />
                  {showClientesDrop && clienteInput.length > 0 && (
                    <div className="absolute z-50 w-full bg-white border border-slate-200 shadow-xl rounded-lg mt-1 max-h-40 overflow-y-auto">
                      {listaClientes.filter(c => c.includes(clienteInput.toUpperCase())).map(nome => {
                        const cli = clientes.find(c => c.nome?.toUpperCase() === nome);
                        return (
                          <div key={nome}
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                cliente_nome: nome,
                                cliente_id: cli?.id || '',
                              }));
                              setClienteInput(nome);
                              setShowClientesDrop(false);
                            }}
                            className="px-3 py-2 text-xs font-bold hover:bg-blue-50 cursor-pointer border-b border-slate-100 flex justify-between">
                            {nome}<Check size={11} className="text-blue-600" />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div>
                  <label className={lbl}>Data de Entrega * {sacPreenchido.data_entrega && <span className="text-emerald-500 normal-case font-black">✓ SAC</span>}</label>
                  <input
                    type="date"
                    value={formData.data_entrega}
                    onChange={e => setFormData(prev => ({ ...prev, data_entrega: e.target.value }))}
                    className={inpCls('data_entrega')}
                    required />
                </div>
                <div>
                  <label className={lbl}>Status</label>
                  <select
                    value={formData.status}
                    onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    className={`${inp} cursor-pointer`}
                    disabled={formData.status === 'NF_EMITIDA' && !isAdmin}>
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_CONFIG[s]?.label}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className={lbl}>Condição de Pagamento</label>
                  <select
                    value={formData.condicao_pagamento}
                    onChange={e => setFormData(prev => ({ ...prev, condicao_pagamento: e.target.value }))}
                    className={`${inp} cursor-pointer`}>
                    <option value="">— Selecione —</option>
                    {condicoes.map(c => <option key={c.id} value={c.descricao}>{c.descricao}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Card 03 · Itens do Pedido */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-center justify-between border-b border-[#0A3D73]/20 pb-2 mb-4">
                <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73]">03 · Itens do Pedido</p>
                {!adicionandoItem && (
                  <button
                    type="button"
                    onClick={() => setAdicionandoItem(true)}
                    className="flex items-center gap-1.5 text-xs font-black uppercase text-[#0A3D73] hover:text-[#082D54] border border-[#0A3D73] rounded-lg px-3 py-1.5 hover:bg-blue-50 transition-all">
                    <Plus size={12} /> Adicionar Item
                  </button>
                )}
              </div>

              {/* Tabela de itens */}
              {itens.length > 0 && (
                <div className="overflow-x-auto mb-4">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-[#0A3D73] text-white text-[10px] font-black uppercase">
                        <th className="px-2 py-2 text-left rounded-tl-lg">FE</th>
                        <th className="px-2 py-2 text-left">Descrição</th>
                        <th className="px-2 py-2 text-right">Qtde</th>
                        <th className="px-2 py-2 text-right">Peso KG</th>
                        <th className="px-2 py-2 text-right">R$/Milh</th>
                        <th className="px-2 py-2 text-right">Fator</th>
                        <th className="px-2 py-2 text-right">Valor Total</th>
                        <th className="px-2 py-2 text-right">Com%</th>
                        <th className="px-2 py-2 text-right">Com R$</th>
                        <th className="px-2 py-2 text-center rounded-tr-lg">×</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itens.map((it, i) => (
                        <tr key={i} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}`}>
                          <td className="px-2 py-2 font-black text-blue-700 font-mono">{it.numero_fe || '—'}</td>
                          <td className="px-2 py-2 font-semibold text-slate-700 max-w-[130px] truncate">{it.item_nome || '—'}</td>
                          <td className="px-2 py-2 text-right font-bold">{(parseInt(it.quantidade) || 0).toLocaleString('pt-BR')}</td>
                          <td className="px-2 py-2 text-right font-bold text-blue-600">{fmt(toNum(it.peso_total))}</td>
                          <td className="px-2 py-2 text-right font-semibold">{brl(toNum(it.preco_milheiro))}</td>
                          <td className="px-2 py-2 text-right font-semibold">R$ {fmt(toNum(it.fator))}</td>
                          <td className="px-2 py-2 text-right font-black text-emerald-700">{brl(toNum(it.valor_total))}</td>
                          <td className="px-2 py-2 text-right font-semibold">{fmt(toNum(it.comissao_percent))}%</td>
                          <td className="px-2 py-2 text-right font-bold text-green-700">{brl(toNum(it.comissao_valor))}</td>
                          <td className="px-2 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => handleRemoverItem(i)}
                              className="text-red-400 hover:text-red-600 transition-colors p-0.5 rounded">
                              <Trash2 size={12} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-[#0A3D73] text-white font-black text-[10px]">
                        <td colSpan={2} className="px-2 py-2 uppercase tracking-wide">
                          {itens.length} {itens.length === 1 ? 'item' : 'itens'}
                        </td>
                        <td className="px-2 py-2 text-right">{totaisItens.quantidade.toLocaleString('pt-BR')}</td>
                        <td className="px-2 py-2 text-right">{fmt(totaisItens.pesoTotal)}</td>
                        <td colSpan={2} />
                        <td className="px-2 py-2 text-right text-emerald-300">{brl(totaisItens.valorTotal)}</td>
                        <td />
                        <td className="px-2 py-2 text-right text-green-300">{brl(totaisItens.comissaoValor)}</td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {!adicionandoItem && itens.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <Package size={28} className="mx-auto mb-2 opacity-30" />
                  <p className="text-xs font-bold">Nenhum item adicionado</p>
                  <p className="text-[10px] mt-0.5">Clique em "Adicionar Item" para começar</p>
                </div>
              )}

              {/* Formulário inline de novo item */}
              {adicionandoItem && (
                <div className="border border-blue-200 rounded-xl p-4 bg-blue-50/30 space-y-3">
                  <p className="text-xs font-black uppercase tracking-wide text-blue-700">Novo Item</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="md:col-span-2">
                      <label className={lbl}>Produto (FE) * {sacPreenchido.numero_fe && <span className="text-emerald-500 normal-case font-black">✓ SAC</span>}</label>
                      <input
                        value={itemEditando.numero_fe}
                        onChange={e => handleItemFEChange(e.target.value)}
                        className={sacPreenchido.numero_fe ? inpCls('numero_fe') : `${inp} border-blue-300`}
                        placeholder="Ex: FE-1234-CX" />
                    </div>
                    <div className="md:col-span-2">
                      <label className={lbl}>Descrição</label>
                      <input
                        value={itemEditando.item_nome}
                        onChange={e => setItemEditando(prev => ({ ...prev, item_nome: e.target.value.toUpperCase() }))}
                        className={inp}
                        placeholder="Auto-preenchido pelo FE" />
                    </div>
                    <div>
                      <label className={lbl}>Quantidade * {sacPreenchido.quantidade && <span className="text-emerald-500 normal-case font-black">✓ SAC</span>}</label>
                      <input
                        type="number"
                        value={itemEditando.quantidade}
                        onChange={e => handleItemQtdeChange(e.target.value)}
                        className={inpCls('quantidade')} />
                    </div>
                    <div>
                      <label className={lbl}>Peso Unit (KG)</label>
                      <div className="h-10 flex items-center px-3 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-500">
                        {itemEditando.peso_unit ? toNum(itemEditando.peso_unit).toFixed(4) : '—'}
                      </div>
                    </div>
                    <div>
                      <label className={lbl}>Peso Total (KG)</label>
                      <div className="h-10 flex items-center px-3 bg-blue-50 border border-blue-200 rounded-lg text-xs font-black text-blue-800">
                        {itemEditando.peso_total || '—'}
                      </div>
                    </div>
                    <div>
                      <label className={lbl}>R$ Milheiro</label>
                      <div className="h-10 flex items-center px-3 bg-slate-50 border border-slate-200 rounded-lg text-xs font-black text-slate-700">
                        {itemEditando.preco_milheiro || '—'}
                      </div>
                    </div>
                    <div>
                      <label className={lbl}>Fator R$/KG</label>
                      <div className="h-10 flex items-center px-3 bg-blue-50 border border-blue-200 rounded-lg text-xs font-black text-blue-800">
                        R$ {itemEditando.fator || '0,00'}
                      </div>
                    </div>
                    <div>
                      <label className={`${lbl} text-emerald-700`}>Valor Total</label>
                      <div className="h-10 flex items-center px-3 bg-emerald-50 border border-emerald-300 rounded-lg text-sm font-black text-emerald-800">
                        R$ {itemEditando.valor_total || '0,00'}
                      </div>
                    </div>
                    <div>
                      <label className={`${lbl} text-green-700`}>Comissão %</label>
                      <input
                        value={itemEditando.comissao_percent}
                        onChange={e => {
                          const pct = toNum(e.target.value);
                          const val = toNum(itemEditando.valor_total);
                          setItemEditando(prev => ({
                            ...prev,
                            comissao_percent: e.target.value,
                            comissao_valor: fmt(val * pct / 100),
                          }));
                        }}
                        className={`${inp} border-green-200`}
                        placeholder="Ex: 2,5" />
                    </div>
                    <div>
                      <label className={`${lbl} text-green-700`}>Comissão R$</label>
                      <div className="h-10 flex items-center px-3 bg-green-50 border border-green-200 rounded-lg text-xs font-black text-green-800">
                        R$ {itemEditando.comissao_valor || '0,00'}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={handleConfirmarItem}
                      className="flex items-center gap-1.5 bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase px-4 py-2 rounded-lg transition-colors">
                      <Check size={12} /> Confirmar Item
                    </button>
                    <button
                      type="button"
                      onClick={() => { setAdicionandoItem(false); setItemEditando(ITEM_INITIAL); }}
                      className="text-xs font-black uppercase px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors">
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>

          {/* ── COL DIREITA ── */}
          <div className="space-y-6">

            {/* Card Resumo do Pedido */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 lg:sticky lg:top-[73px]">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">Resumo do Pedido</p>
              <div className="space-y-4">

                {/* Cliente */}
                <div className="flex items-start gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-[#EFF6FF] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Package size={13} className="text-[#0A3D73]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Cliente</p>
                    <p className="text-sm font-black text-slate-700 truncate">{formData.cliente_nome || '—'}</p>
                  </div>
                </div>

                {/* Lista resumida de itens */}
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 space-y-1">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2">
                    Itens ({itens.length})
                  </p>
                  {itens.length === 0 ? (
                    <p className="text-[10px] text-slate-400 font-semibold">Nenhum item adicionado</p>
                  ) : (
                    itens.map((it, i) => (
                      <div key={i} className="flex justify-between items-center text-[10px]">
                        <span className="font-black text-blue-700 font-mono">{it.numero_fe || '—'}</span>
                        <span className="font-bold text-slate-500 truncate mx-2 max-w-[80px]">{it.item_nome || '—'}</span>
                        <span className="font-black text-emerald-700 whitespace-nowrap">{brl(toNum(it.valor_total))}</span>
                      </div>
                    ))
                  )}
                </div>

                {/* Valor Total */}
                <div className="bg-[#DCFCE7] rounded-xl p-4 text-center border border-emerald-200">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700/70 mb-1 flex items-center justify-center gap-1">
                    <DollarSign size={10} /> Valor Total
                  </p>
                  <p className="text-2xl font-black text-emerald-800">{brl(totaisItens.valorTotal)}</p>
                </div>

                {/* Comissão */}
                <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp size={14} className="text-amber-600" />
                    <p className="text-[10px] font-bold uppercase tracking-wide text-amber-700">Comissão</p>
                  </div>
                  <p className="text-sm font-black text-amber-800">{brl(totaisItens.comissaoValor)}</p>
                </div>

                {/* Status */}
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Status</span>
                  <span className={`px-3 py-1 text-xs font-black border rounded-full ${statusCfg.cls}`}>
                    {statusCfg.label}
                  </span>
                </div>

                {/* Representada */}
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Representada</p>
                  <p className="text-sm font-semibold text-slate-700">{formData.representada_nome || '—'}</p>
                </div>

                {/* Peso Total */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Weight size={13} className="text-slate-400" />
                    <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Peso Total</span>
                  </div>
                  <p className="text-sm font-black text-slate-700">{fmt(totaisItens.pesoTotal)} KG</p>
                </div>

              </div>
            </div>

          </div>
        </div>
      </div>
    </form>
  );
};

export default PedidoForm;
